import type { ApiResponse } from '@shared/types'
import { QUEUE_DOMAIN } from 'v2/configs/constants'
import { BackendServiceName } from 'v2/types'
import type {
  Channels,
  ConversationStatus,
  InitiateSmsConversationPayload,
  InitiateWhatsAppConversationPayload,
  RNewConversation,
  TransferPayload,
} from 'v2/types/api/conversations'
import type {
  CreateTemplatePayload,
  RWhatsAppMediaUploadResponse,
  RWhatsappTemplate,
  RWhatsappTemplatesResponse,
  UpdateTemplatePayload,
  WhatsAppAccount,
} from 'v2/types/api/settings'
import { MonitorStates } from 'v2/types/call'
import { logger } from 'v2/utils/logger'
import { createService } from './create-service'

interface AsyncIncomingMessagePayload {
  AuthID: string
  MessageUUID: string
  From: string
  To: string
  Body?: string // for whatsapp
  Text?: string // for sms
  Type: string
  ContentType: string
  PhloUUID: string
}

const EXECUTION_ENDPOINTS = {
  generateToken: '/chat/generateToken',
  whatsappTemplates: '/whatsapp/templates',
  whatsappTemplatesConfig: '/whatsapp/templates/config',
  whatsappMediaUpload: (wabaId: string) => `/whatsapp/templates/media-upload/${wabaId}`,
  createWhatsAppTemplate: (wabaId: string) => `/whatsapp/templates/${wabaId}`,
  updateWhatsAppTemplate: (wabaId: string, templateId: string) =>
    `/whatsapp/templates/${wabaId}/${templateId}`,
  deleteWhatsAppTemplate: (wabaId: string, templateId: string) =>
    `/whatsapp/templates/${wabaId}/${templateId}`,
  getInvitedContacts: (uuid: string) => `/invite/invitedcontacts/${uuid}`,
  acceptNewConversation: (uuid: string) => `/invite/accept/${uuid}`,
  acceptAsyncTransfer: (uuid: string) => `/invite/accept_transfer/${uuid}`,
  declineNewConversation: (uuid: string) => `/invite/decline/${uuid}`,
  getWhatsAppMedia: (senderId: string, fileId: string) =>
    `execution_service/v1/whatsapp/${senderId}/media/${fileId}`,
  initiateWhatsAppConversation: '/conversation/outbound/whatsapp/initiate',
  initiateSmsConversation: '/conversation/outbound/message/initiate',
  transfer: (channel: string, transferType: string) => `/transfer/${channel}/${transferType}`,
  cancelTransfer: (channel: string, transferType: string) =>
    `/transfer/${channel}/${transferType}/stop`,
  leaveTransfer: '/transfer/leave_transfer',
  monitorActions: (conversationId: string, action: MonitorStates) =>
    `/monitor/${conversationId}/actions?${action}=true`,
  leaveMonitor: (conversationId: string) => `/monitor/${conversationId}/leave`,
  whatsappIncomingMessage: (authId: string) => `/whatsapp/account/${authId}/incoming`,
  messageIncomingMessage: (authId: string, phloUUID: string) =>
    `/message/account/${authId}/incoming_message/${phloUUID}`,
}

const getWhatsAppMediaUrl = async (
  senderId: string,
  fileId: string,
  queueService: ReturnType<typeof createService>,
  contactUuid?: string,
): Promise<Blob> => {
  try {
    // Naming the conversation lets the execution service sign the fetch with the
    // account that acts for it (a tagged run's subaccount); without it, the org pair.
    const response = await queueService.get(
      EXECUTION_ENDPOINTS.getWhatsAppMedia(senderId, fileId),
      contactUuid ? { contact_uuid: contactUuid } : {},
      { responseType: 'blob' },
    )

    if (response instanceof Blob) return response

    if (
      response &&
      typeof response === 'object' &&
      'data' in response &&
      response.data instanceof Blob
    ) {
      return response.data
    }

    throw new Error('Unexpected response format')
  } catch (error) {
    logger.error('Error in getWhatsAppMedia:', error)
    throw error
  }
}

interface ExecutionService {
  getWhatsAppTemplates: (
    params: Record<string, string>,
  ) => Promise<ApiResponse<RWhatsappTemplatesResponse[]>>
  getWhatsAppTemplatesConfig: () => Promise<ApiResponse<WhatsAppAccount[]>>
  uploadWhatsAppMedia: (
    wabaId: string,
    payload: { media_url: string },
  ) => Promise<ApiResponse<RWhatsAppMediaUploadResponse>>
  createWhatsAppTemplate: (
    wabaId: string,
    payload: CreateTemplatePayload,
  ) => Promise<ApiResponse<void>>
  updateWhatsAppTemplate: (
    wabaId: string,
    templateId: string,
    payload: UpdateTemplatePayload,
  ) => Promise<ApiResponse<RWhatsappTemplate>>
  deleteWhatsAppTemplate: (wabaId: string, templateId: string) => Promise<ApiResponse<void>>
  generateToken: (uuid: string) => Promise<ApiResponse<string>>
  getInvitedContacts: (uuid: string) => Promise<ApiResponse<RNewConversation[]>>
  acceptNewConversation: (
    uuid: string,
    channel?: Channels,
    contactId?: string,
    isAgentCreatedTask?: boolean,
  ) => Promise<ApiResponse<void>>
  acceptAsyncTransfer: (
    uuid: string,
    channel?: Channels,
    contact_id?: string,
  ) => Promise<ApiResponse<void>>
  declineNewConversation: (
    uuid: string,
    channel: Channels,
    isAgentCreatedTask?: boolean,
  ) => Promise<ApiResponse<void>>
  getWhatsAppMedia: (senderId: string, fileId: string, contactUuid?: string) => Promise<Blob>
  initiateWhatsAppConversation: (
    payload: InitiateWhatsAppConversationPayload,
  ) => Promise<ApiResponse<void>>
  initiateSmsConversation: (payload: InitiateSmsConversationPayload) => Promise<ApiResponse<void>>
  transfer: (
    channel: string,
    transferType: 'warm' | 'cold',
    payload: TransferPayload,
  ) => Promise<ApiResponse<void>>
  cancelTransfer: (
    channel: Channels,
    transferType: 'warm' | 'cold',
    payload: Partial<TransferPayload>,
  ) => Promise<ApiResponse<void>>
  leaveTransfer: (
    contactUuid: string,
    leaveType: 'leave_primary' | 'drop_secondary' | 'close_conversation' | 'leave_secondary',
    channel: string,
  ) => Promise<ApiResponse<void>>
  notifyMonitoring: (conversationId: string) => Promise<ApiResponse<void>>
  toggleSupervisorMute: (conversationId: string, mute: boolean) => Promise<ApiResponse<void>>
  requestHelp: (conversationId: string) => Promise<ApiResponse<void>>
  rejectAssistance: (conversationId: string) => Promise<ApiResponse<void>>
  leaveMonitoring: (
    conversationId: string,
    payload: {
      state: ConversationStatus
      agent_state: MonitorStates
    },
  ) => Promise<ApiResponse<void>>
  triggerWhatsAppIncomingMessage: (
    payload: AsyncIncomingMessagePayload,
  ) => Promise<ApiResponse<void>>
  triggerMessageIncomingMessage: (
    payload: AsyncIncomingMessagePayload,
  ) => Promise<ApiResponse<void>>
}

const serviceFactory = (): ExecutionService => {
  const service = createService('', true, BackendServiceName.execution, 'v1')
  const queueService = createService('', true, BackendServiceName.default, '', QUEUE_DOMAIN)
  return {
    generateToken: (uuid: string) =>
      service.post(EXECUTION_ENDPOINTS.generateToken, { authorized_uuid: uuid }),
    getWhatsAppTemplates: (params) => service.get(EXECUTION_ENDPOINTS.whatsappTemplates, params),
    getWhatsAppTemplatesConfig: () => service.get(EXECUTION_ENDPOINTS.whatsappTemplatesConfig),
    uploadWhatsAppMedia: (wabaId, payload) =>
      service.post(EXECUTION_ENDPOINTS.whatsappMediaUpload(wabaId), payload),
    createWhatsAppTemplate: (wabaId, payload) =>
      service.post(EXECUTION_ENDPOINTS.createWhatsAppTemplate(wabaId), payload),
    updateWhatsAppTemplate: (wabaId, templateId, payload) =>
      service.post(EXECUTION_ENDPOINTS.updateWhatsAppTemplate(wabaId, templateId), payload),
    deleteWhatsAppTemplate: (wabaId, templateId) =>
      service.delete(EXECUTION_ENDPOINTS.deleteWhatsAppTemplate(wabaId, templateId)),
    getInvitedContacts: (uuid: string) => service.get(EXECUTION_ENDPOINTS.getInvitedContacts(uuid)),
    acceptNewConversation: (
      uuid: string,
      channel?: Channels,
      contactId?: string,
      isAgentCreatedTask?: boolean,
    ) =>
      service.post(EXECUTION_ENDPOINTS.acceptNewConversation(uuid), {
        channel,
        from_number: contactId,
        is_agent_created_task: isAgentCreatedTask,
      }),
    acceptAsyncTransfer: (uuid: string, channel?: Channels, contact_id?: string) =>
      service.post(EXECUTION_ENDPOINTS.acceptAsyncTransfer(uuid), {
        channel,
        contact_id,
      }),
    declineNewConversation: (uuid: string, channel: Channels, isAgentCreatedTask = false) =>
      service.post(EXECUTION_ENDPOINTS.declineNewConversation(uuid), {
        channel,
        is_agent_created_task: isAgentCreatedTask,
      }),
    getWhatsAppMedia: (senderId, fileId, contactUuid) =>
      getWhatsAppMediaUrl(senderId, fileId, queueService, contactUuid),
    initiateWhatsAppConversation: (payload) =>
      service.post(EXECUTION_ENDPOINTS.initiateWhatsAppConversation, payload),
    initiateSmsConversation: (payload) =>
      service.post(EXECUTION_ENDPOINTS.initiateSmsConversation, payload),
    transfer: (channel, transferType, payload) =>
      service.post(EXECUTION_ENDPOINTS.transfer(channel, transferType), payload),
    cancelTransfer: (channel, transferType, payload) =>
      service.post(EXECUTION_ENDPOINTS.cancelTransfer(channel, transferType), payload),
    leaveTransfer: (contactUuid, leaveType, channel) =>
      service.post(EXECUTION_ENDPOINTS.leaveTransfer, {
        contact_uuid: contactUuid,
        leave_type: leaveType,
        channel,
      }),
    notifyMonitoring: (conversationId) =>
      service.patch(EXECUTION_ENDPOINTS.monitorActions(conversationId, MonitorStates.NOTIFY), {}),
    toggleSupervisorMute: (conversationId, mute) =>
      service.patch(EXECUTION_ENDPOINTS.monitorActions(conversationId, MonitorStates.MUTE), {
        mute,
      }),
    requestHelp: (conversationId) =>
      service.patch(
        EXECUTION_ENDPOINTS.monitorActions(conversationId, MonitorStates.REQUEST_FOR_HELP),
        {},
      ),
    rejectAssistance: (conversationId) =>
      service.patch(
        EXECUTION_ENDPOINTS.monitorActions(conversationId, MonitorStates.ASSISTANCE_REJECT),
        {},
      ),
    leaveMonitoring: (conversationId, payload) =>
      service.post(EXECUTION_ENDPOINTS.leaveMonitor(conversationId), payload),
    triggerWhatsAppIncomingMessage: (payload) =>
      service.post(EXECUTION_ENDPOINTS.whatsappIncomingMessage(payload.AuthID), payload, {
        ispg: 'true',
      }),
    triggerMessageIncomingMessage: (payload) =>
      service.post(
        EXECUTION_ENDPOINTS.messageIncomingMessage(payload.AuthID, payload.PhloUUID),
        payload,
        { ispg: 'true' },
      ),
  }
}

export const executionService = serviceFactory()
